/**
 * Thin fetch wrapper that talks to the API host (dev.api.ampersandbrands.com /
 * api.ampersandbrands.com) with cookie credentials. The host comes from
 * `API_BASE` in `apiBase.ts`; every caller builds its URL as `${API_BASE}/v1/...`.
 * `credentials: 'include'` sends the `.ampersandbrands.com` session cookie
 * cross-origin.
 *
 * Copied from the cockpit UI's lib/api.ts (same shared API platform, same
 * cross-origin cookie session).
 */

export type ApiError = Error & {
  status: number;
  body: unknown;
};

function makeError(status: number, body: unknown, message: string): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');

  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  // CloudFront's 403/404 error-response rule can return index.html with status
  // 200 when the origin denies the request. Detect HTML coming back on a JSON
  // API call and treat it as a 401 so AuthGate redirects to login rather than
  // silently accepting the HTML as valid session data.
  const contentType = res.headers.get('content-type') ?? '';
  if (res.ok && contentType.includes('text/html')) {
    throw makeError(401, null, `API returned HTML on ${path} (session may have expired)`);
  }

  const body = await readBody(res);
  if (!res.ok) {
    throw makeError(res.status, body, `API ${res.status} on ${path}`);
  }
  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'GET' });
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export { API_BASE } from './apiBase';
